<template>
  <div class="card shadow-sm mb-4 border-success">
    <div class="card-header bg-success text-white d-flex align-items-center justify-content-between flex-wrap gap-2">
      <h3 class="h6 mb-0">
        <i class="bi bi-magic me-2"></i>Nueva receta
      </h3>
      <div class="btn-group btn-group-sm" role="group" aria-label="Modo de creación">
        <button
          type="button"
          class="btn"
          :class="modo === 'ia' ? 'btn-light' : 'btn-outline-light'"
          @click="modo = 'ia'"
        >
          Generar con IA
        </button>
        <button
          type="button"
          class="btn"
          :class="modo === 'manual' ? 'btn-light' : 'btn-outline-light'"
          @click="activarManual"
        >
          Escribir yo
        </button>
      </div>
    </div>
    <div class="card-body">
      <p class="text-muted small mb-3">
        Pide una receta al estilo Metamorfosis o escríbela tú. Al guardarla queda en
        <strong>Mías</strong> y puedes abrirla desde el listado.
      </p>

      <div v-if="modo === 'ia' && !llmConfigured" class="alert alert-warning small py-2">
        <i class="bi bi-exclamation-triangle me-1"></i>
        Falta <code>OPENAI_API_KEY</code> en el servidor. Puedes escribir la receta a mano.
      </div>

      <form v-if="modo === 'ia'" class="row g-3" @submit.prevent="generar">
        <div class="col-12">
          <label class="form-label fw-semibold">Qué quieres cocinar *</label>
          <input
            v-model="form.prompt"
            type="text"
            class="form-control"
            maxlength="800"
            required
            placeholder="Ej: cena rápida con huevos y espinacas"
            :disabled="generando || !llmConfigured"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">Tipo</label>
          <select v-model="form.tipo" class="form-select" :disabled="generando || !llmConfigured">
            <option value="">Sin preferencia</option>
            <option v-for="tipo in tipos" :key="tipo" :value="tipo">{{ etiquetaTipo(tipo) }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Raciones</label>
          <input
            v-model.number="form.raciones"
            type="number"
            min="1"
            max="12"
            class="form-control"
            :disabled="generando || !llmConfigured"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label">Ingredientes que tienes</label>
          <input
            v-model="form.ingredientes"
            type="text"
            class="form-control"
            maxlength="400"
            placeholder="Opcional: salmón, limón…"
            :disabled="generando || !llmConfigured"
          />
        </div>
        <div class="col-12">
          <label class="form-label">Restricciones</label>
          <input
            v-model="form.restricciones"
            type="text"
            class="form-control"
            maxlength="400"
            placeholder="Opcional: sin gluten, poca grasa…"
            :disabled="generando || !llmConfigured"
          />
        </div>
        <div class="col-12 d-flex flex-wrap gap-2">
          <button
            type="submit"
            class="btn btn-success"
            :disabled="generando || !llmConfigured || !form.prompt.trim()"
          >
            <span v-if="generando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-magic me-1"></i>
            {{ generando ? 'Generando…' : 'Generar receta' }}
          </button>
          <button
            v-if="borrador"
            type="button"
            class="btn btn-outline-secondary"
            :disabled="generando || guardando"
            @click="limpiarBorrador"
          >
            Descartar
          </button>
        </div>
      </form>

      <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>
      <div v-if="mensaje" class="alert alert-success mt-3 mb-0">{{ mensaje }}</div>

      <div v-if="borrador" class="receta-borrador mt-4">
        <h4 class="h6 mb-3">
          <i class="bi bi-pencil-square me-1"></i>
          {{ modo === 'ia' ? 'Revisa y guarda' : 'Escribe la receta' }}
        </h4>
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">Título *</label>
            <input v-model="borrador.titulo" type="text" class="form-control" maxlength="120" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Tipo</label>
            <select v-model="borrador.tipo" class="form-select">
              <option value="">Sin tipo</option>
              <option v-for="tipo in tipos" :key="tipo" :value="tipo">{{ etiquetaTipo(tipo) }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Raciones</label>
            <input v-model.number="borrador.raciones" type="number" min="1" max="12" class="form-control" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Tiempo (min)</label>
            <input v-model.number="borrador.tiempo_min" type="number" min="1" max="240" class="form-control" />
          </div>
          <div class="col-md-6">
            <label class="form-label">Ingredientes * <span class="text-muted fw-normal">(uno por línea)</span></label>
            <textarea v-model="ingredientesTexto" class="form-control" rows="8"></textarea>
          </div>
          <div class="col-md-6">
            <label class="form-label">Pasos * <span class="text-muted fw-normal">(uno por línea)</span></label>
            <textarea v-model="pasosTexto" class="form-control" rows="8"></textarea>
          </div>
          <div class="col-12">
            <label class="form-label">Notas</label>
            <input v-model="borrador.notas" type="text" class="form-control" maxlength="400" />
          </div>
          <div class="col-12">
            <button type="button" class="btn btn-success" :disabled="guardando || !puedeGuardar" @click="guardar">
              <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-check2-circle me-1"></i>
              {{ guardando ? 'Guardando…' : 'Guardar en mis recetas' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import axios from '../config/axios';
import { etiquetaTipo, lineasALista } from '../utils/recetasForm';

const TIPOS_FALLBACK = ['desayuno', 'comida', 'cena', 'snack', 'guarnicion'];

function borradorVacio() {
  return {
    titulo: '',
    tipo: '',
    raciones: 2,
    tiempo_min: null,
    ingredientes: [],
    pasos: [],
    notas: '',
    prompt_origen: '',
  };
}

export default {
  name: 'RecetaGenerar',
  props: {
    llmConfigured: { type: Boolean, default: false },
    tipos: { type: Array, default: () => TIPOS_FALLBACK },
  },
  emits: ['guardada'],
  setup(props, { emit }) {
    const modo = ref('ia');
    const generando = ref(false);
    const guardando = ref(false);
    const error = ref('');
    const mensaje = ref('');
    const borrador = ref(null);
    const ingredientesTexto = ref('');
    const pasosTexto = ref('');
    const form = ref({
      prompt: '',
      tipo: '',
      raciones: 2,
      ingredientes: '',
      restricciones: '',
    });

    const puedeGuardar = computed(() => {
      if (!borrador.value) return false;
      return (
        borrador.value.titulo.trim().length >= 3 &&
        lineasALista(ingredientesTexto.value).length >= 2 &&
        lineasALista(pasosTexto.value).length >= 2
      );
    });

    const aplicarBorrador = (data, promptOrigen = '') => {
      borrador.value = {
        titulo: data.titulo || '',
        tipo: data.tipo || '',
        raciones: data.raciones || 2,
        tiempo_min: data.tiempo_min || null,
        ingredientes: data.ingredientes || [],
        pasos: data.pasos || [],
        notas: data.notas || '',
        prompt_origen: data.prompt_origen || promptOrigen,
      };
      ingredientesTexto.value = (data.ingredientes || []).join('\n');
      pasosTexto.value = (data.pasos || []).join('\n');
    };

    const activarManual = () => {
      modo.value = 'manual';
      error.value = '';
      mensaje.value = '';
      if (!borrador.value) {
        aplicarBorrador(borradorVacio());
      }
    };

    const limpiarBorrador = () => {
      borrador.value = modo.value === 'manual' ? borradorVacio() : null;
      ingredientesTexto.value = '';
      pasosTexto.value = '';
      mensaje.value = '';
    };

    const generar = async () => {
      generando.value = true;
      error.value = '';
      mensaje.value = '';
      try {
        const { data } = await axios.post(
          '/api/recetas/generar',
          {
            prompt: form.value.prompt.trim(),
            tipo: form.value.tipo || undefined,
            raciones: form.value.raciones || undefined,
            ingredientes: form.value.ingredientes.trim() || undefined,
            restricciones: form.value.restricciones.trim() || undefined,
          },
          { timeout: 60000 }
        );
        aplicarBorrador(data, form.value.prompt.trim());
        mensaje.value = 'Receta generada. Revísala y guárdala si te encaja.';
      } catch (err) {
        error.value = err.response?.data?.error || 'No se pudo generar la receta';
      } finally {
        generando.value = false;
      }
    };

    const guardar = async () => {
      if (!borrador.value || !puedeGuardar.value) return;
      guardando.value = true;
      error.value = '';
      mensaje.value = '';
      try {
        const { data } = await axios.post('/api/recetas', {
          titulo: borrador.value.titulo.trim(),
          tipo: borrador.value.tipo || null,
          raciones: borrador.value.raciones,
          tiempo_min: borrador.value.tiempo_min || null,
          ingredientes: lineasALista(ingredientesTexto.value),
          pasos: lineasALista(pasosTexto.value),
          notas: borrador.value.notas.trim() || null,
          prompt_origen: borrador.value.prompt_origen || form.value.prompt.trim() || null,
        });
        form.value = {
          prompt: '',
          tipo: form.value.tipo,
          raciones: form.value.raciones,
          ingredientes: '',
          restricciones: '',
        };
        limpiarBorrador();
        mensaje.value = 'Receta guardada en Mías.';
        emit('guardada', data);
      } catch (err) {
        error.value = err.response?.data?.error || 'No se pudo guardar la receta';
      } finally {
        guardando.value = false;
      }
    };

    return {
      modo,
      form,
      generando,
      guardando,
      error,
      mensaje,
      borrador,
      ingredientesTexto,
      pasosTexto,
      puedeGuardar,
      etiquetaTipo,
      activarManual,
      limpiarBorrador,
      generar,
      guardar,
    };
  },
};
</script>

<style scoped>
.receta-borrador {
  border-top: 1px dashed #ced4da;
  padding-top: 1.25rem;
}
</style>
